import { supabase } from '../lib/supabase';
import { Product } from '../types/database.types';

export const productManagementService = {
  // Get all products (for admin/CMS)
  async getAll(): Promise<{ data: Product[] | null; error: Error | null }> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  // Get single product
  async getById(productId: string): Promise<{ data: Product | null; error: Error | null }> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', productId)
      .single();
    
    return { data, error };
  },

  // Create new product
  async create(product: {
    part_id: string;
    description: string;
    price: number;
    category?: string;
    image_url?: string;
    image_alt?: string;
  }): Promise<{ data: Product | null; error: Error | null }> {
    const { data, error } = await supabase
      .from('products')
      .insert({
        part_id: product.part_id,
        description: product.description,
        price: product.price,
        category: product.category,
        image_url: product.image_url,
        image_alt: product.image_alt,
        is_active: true,
      })
      .select()
      .single();
    
    return { data, error };
  },

  // Update product
  async updateProduct(
    productId: string,
    updates: {
      part_id?: string;
      description?: string;
      price?: number;
      category?: string;
      image_url?: string;
      image_alt?: string;
      is_active?: boolean;
      sync_to_website?: boolean;
      quickbooks_item_id?: string;
      last_synced_from_quickbooks_at?: string;
    }
  ): Promise<{ data: Product | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', productId)
        .select();

      if (error) throw error;
      
      // Check if any rows were updated
      if (!data || data.length === 0) {
        throw new Error('Product not found or you do not have permission to update it');
      }

      return { data: data[0], error: null };
    } catch (error) {
      console.error('Error updating product:', error);
      return { data: null, error: error as Error };
    }
  },

  async bulkUpdateProductSyncStatus(productIds: string[], syncToWebsite: boolean) {
    try {
      const { data, error } = await supabase
        .from('products')
        .update({ sync_to_website: syncToWebsite })
        .in('id', productIds)
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error bulk updating product sync status:', error);
      throw error;
    }
  },

  async getProductsByQuickBooksItemId(quickbooksItemId: string) {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('quickbooks_item_id', quickbooksItemId);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching products by QuickBooks item ID:', error);
      throw error;
    }
  },

  async getWebsiteSyncedProducts() {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('sync_to_website', true)
        .eq('is_active', true)
        .order('part_id', { ascending: true });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching website synced products:', error);
      throw error;
    }
  },

  // Get products by category
  async getByCategory(category: string): Promise<{ data: Product[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .neq('sync_to_website', false)
        .ilike('category', `%${category}%`)
        .order('part_id', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Delete product
  async delete(productId: string): Promise<{ error: Error | null }> {
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId);
    
    return { error };
  },

  // Upload product image to Supabase storage
  async uploadImage(
    file: File,
    productId: string
  ): Promise<{ data: { url: string } | null; error: Error | null }> {
    try {
      console.log('📤 Upload service: Starting upload for product:', productId);
      console.log('📁 File metadata:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString()
      });

      const fileExt = file.name.split('.').pop();
      const fileName = `${productId}-${Date.now()}.${fileExt}`;
      const filePath = `product-images/${fileName}`;

      console.log('🎯 Target storage path:', filePath);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('Resources')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: '3600', // Cache for 1 hour
        });

      if (uploadError) {
        console.error('❌ Storage upload error:', uploadError);
        throw uploadError;
      }

      console.log('✅ File uploaded successfully to storage:', uploadData);

      // Get public URL - this is the critical step for image rendering
      const { data: { publicUrl } } = supabase.storage
        .from('Resources')
        .getPublicUrl(filePath);

      console.log('🔗 Generated public URL:', publicUrl);

      // Verify the bucket is public
      const { data: bucketData } = await supabase.storage
        .getBucket('Resources');
      
      console.log('🪣 Bucket configuration:', {
        name: bucketData?.name,
        public: bucketData?.public,
        id: bucketData?.id
      });

      if (!bucketData?.public) {
        console.warn('⚠️ WARNING: Resources bucket is not public! Images may not load.');
      }

      return { data: { url: publicUrl }, error: null };
    } catch (error) {
      console.error('💥 Upload service: Critical error:', error);
      return { data: null, error: error as Error };
    }
  },

  // Delete product image
  async deleteImage(imageUrl: string): Promise<{ error: Error | null }> {
    try {
      console.log('🗑️ Delete service: Starting deletion for:', imageUrl);
      
      // Extract file path from URL
      const urlParts = imageUrl.split('/Resources/');
      if (urlParts.length < 2) {
        throw new Error('Invalid image URL format');
      }
      const filePath = urlParts[1];

      console.log('🎯 Target deletion path:', filePath);

      const { error } = await supabase.storage
        .from('Resources')
        .remove([filePath]);

      if (error) {
        console.error('❌ Storage deletion error:', error);
      } else {
        console.log('✅ File deleted successfully from storage');
      }

      return { error };
    } catch (error) {
      console.error('💥 Delete service: Critical error:', error);
      return { error: error as Error };
    }
  },

  // Bulk upload products from CSV
  async bulkUpload(
    csvData: Array<Record<string, string>>,
    imageFiles?: Map<string, File>
  ): Promise<{
    data: {
      successful: number;
      failed: number;
      results: Array<{
        partId: string;
        status: 'success' | 'error';
        message: string;
        product?: Product;
      }>;
    } | null;
    error: Error | null;
  }> {
    try {
      console.log('📦 Starting bulk upload for', csvData.length, 'products');
      const results: Array<{
        partId: string;
        status: 'success' | 'error';
        message: string;
        product?: Product;
      }> = [];
      let successful = 0;
      let failed = 0;

      for (const row of csvData) {
        try {
          const partId = row.part_id || row.Part_ID || row['Part ID'];
          if (!partId) {
            failed++;
            results.push({
              partId: 'unknown',
              status: 'error',
              message: 'Missing part_id in CSV row',
            });
            continue;
          }

          // Check if product exists
          const { data: existingProduct } = await supabase
            .from('products')
            .select('*')
            .eq('part_id', partId)
            .single();

          // Prepare product data from CSV row
          const productData: any = {};

          // Map CSV columns to database columns (case-insensitive)
          if (row.description || row.Description) {
            productData.description = row.description || row.Description;
          }
          if (row.price || row.Price) {
            productData.price = parseFloat(row.price || row.Price);
          }
          if (row.category || row.Category) {
            productData.category = row.category || row.Category;
          }
          if (row.image_alt || row.Image_Alt || row['Image Alt']) {
            productData.image_alt = row.image_alt || row.Image_Alt || row['Image Alt'];
          }

          // Handle image URL from CSV or image file upload
          let imageUrl = row.image_url || row.Image_URL || row['Image URL'];
          
          if (imageFiles && imageFiles.has(partId)) {
            const imageFile = imageFiles.get(partId)!;
            const tempId = existingProduct?.id || `temp-${partId}-${Date.now()}`;
            const { data: uploadData, error: uploadError } = await this.uploadImage(imageFile, tempId);
            
            if (uploadError) {
              console.warn(`⚠️ Image upload failed for ${partId}:`, uploadError.message);
            } else if (uploadData?.url) {
              imageUrl = uploadData.url;
            }
          }

          if (imageUrl) {
            productData.image_url = imageUrl;
          }

          if (existingProduct) {
            // Update existing product - only update fields present in CSV
            if (Object.keys(productData).length === 0) {
              results.push({
                partId,
                status: 'error',
                message: 'No fields to update (all columns empty)',
              });
              failed++;
              continue;
            }

            const { data: updatedProduct, error: updateError } = await this.updateProduct(
              existingProduct.id,
              productData
            );

            if (updateError) {
              throw updateError;
            }

            successful++;
            results.push({
              partId,
              status: 'success',
              message: 'Product updated successfully',
              product: updatedProduct || undefined,
            });
          } else {
            // Create new product - require all mandatory fields
            if (!productData.description || productData.price === undefined) {
              failed++;
              results.push({
                partId,
                status: 'error',
                message: 'Missing required fields (description, price) for new product',
              });
              continue;
            }

            const { data: newProduct, error: createError } = await this.create({
              part_id: partId,
              description: productData.description,
              price: productData.price,
              category: productData.category,
              image_url: productData.image_url,
              image_alt: productData.image_alt,
            });

            if (createError) {
              throw createError;
            }

            successful++;
            results.push({
              partId,
              status: 'success',
              message: 'Product created successfully',
              product: newProduct || undefined,
            });
          }
        } catch (rowError) {
          failed++;
          results.push({
            partId: row.part_id || row.Part_ID || 'unknown',
            status: 'error',
            message: rowError instanceof Error ? rowError.message : 'Unknown error',
          });
        }
      }

      console.log('✅ Bulk upload complete:', { successful, failed });
      return {
        data: {
          successful,
          failed,
          results,
        },
        error: null,
      };
    } catch (error) {
      console.error('💥 Bulk upload error:', error);
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  // Validate CSV structure
  validateCSVStructure(csvData: Array<Record<string, string>>): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!csvData || csvData.length === 0) {
      errors.push('CSV file is empty');
      return { valid: false, errors };
    }

    // Check for at least one required field (part_id)
    const firstRow = csvData[0];
    const hasPartId = firstRow.part_id || firstRow.Part_ID || firstRow['Part ID'];
    
    if (!hasPartId) {
      errors.push('CSV must contain a "part_id" or "Part ID" column');
    }

    // Validate data types for rows that have values
    csvData.forEach((row, index) => {
      const price = row.price || row.Price;
      if (price && isNaN(parseFloat(price))) {
        errors.push(`Row ${index + 1}: Price must be a valid number`);
      }
    });

    return {
      valid: errors.length === 0,
      errors,
    };
  },
};